import { useMemo } from 'react';
import { format } from 'date-fns';
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { DataTable } from '@/components/common/data-table/data-table';
import { Badge } from '@/components/ui/badge';
import type { Transaction } from '@erp/rpc';

interface TransactionsTableProps {
  transactions: Transaction[];
}

export default function TransactionsTable({
  transactions,
}: TransactionsTableProps) {
  const columns: ColumnDef<Transaction>[] = useMemo(
    () => [
      {
        accessorKey: 'transactionDate',
        header: 'Date',
        cell: ({ getValue }) => {
          const date = getValue<string>();
          return (
            <span className="text-sm text-muted-foreground">
              {date ? format(new Date(date), 'MM/dd/yyyy') : '—'}
            </span>
          );
        },
        size: 140,
      },
      {
        id: 'debit',
        header: 'Debit',
        cell: ({ row }) => {
          const amount = Number(row.original.amount ?? '0');
          const currency = row.original.currency || 'USD';
          const direction = row.original.direction;
          const validAmount = Number.isFinite(amount) ? amount : 0;

          if (direction !== 'debit') {
            return <span className="text-sm text-muted-foreground">—</span>;
          }

          const displayAmount = Math.abs(validAmount);
          const formattedAmount = displayAmount.toLocaleString(undefined, {
            style: 'currency',
            currency,
            minimumFractionDigits: 2,
          });

          return (
            <span className="text-sm font-semibold">({formattedAmount})</span>
          );
        },
        size: 140,
      },
      {
        id: 'credit',
        header: 'Credit',
        cell: ({ row }) => {
          const amount = Number(row.original.amount ?? '0');
          const currency = row.original.currency || 'USD';
          const direction = row.original.direction;
          const validAmount = Number.isFinite(amount) ? amount : 0;

          if (direction !== 'credit') {
            return <span className="text-sm text-muted-foreground">—</span>;
          }

          const displayAmount = Math.abs(validAmount);
          const formattedAmount = displayAmount.toLocaleString(undefined, {
            style: 'currency',
            currency,
            minimumFractionDigits: 2,
          });

          return (
            <span className="text-sm font-semibold">{formattedAmount}</span>
          );
        },
        size: 140,
      },
      {
        accessorKey: 'merchantName',
        header: 'Merchant',
        cell: ({ getValue }) => {
          const merchant = getValue<string | null>();
          return <span className="text-sm">{merchant || '—'}</span>;
        },
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ getValue }) => {
          const desc = getValue<string | null>();
          return <span className="text-sm">{desc || '—'}</span>;
        },
      },
      {
        accessorKey: 'accountName',
        header: 'Account',
        cell: ({ getValue }) => {
          const account = getValue<string | null>();
          return (
            <span className="text-sm text-muted-foreground">
              {account || '—'}
            </span>
          );
        },
        size: 160,
      },
      {
        accessorKey: 'pending',
        header: 'Status',
        cell: ({ getValue }) => {
          const pending = getValue<boolean>();
          return pending ? (
            <Badge variant="outline">Pending</Badge>
          ) : (
            <Badge variant="default">Posted</Badge>
          );
        },
        size: 120,
      },
    ],
    [],
  );

  const table = useReactTable({
    data: transactions,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-4">
      <DataTable table={table} />
      {/* TODO: Add expandable row previews for associated payment */}
    </div>
  );
}
